import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { PlatformUser } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Button } from '@/app/components/ui/Button';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';

/**
 * People › All users (`/manage/users`, was the admin section of the normal
 * app's `UsersPage`). Every identity on the platform, most of which are not a
 * member of any company or department — `/users/all` is the one listing that
 * does not depend on affiliation. The company- and department-scoped member
 * lists stay on the company/department pages themselves (§ Beheer ›
 * Companies, and the normal app for WP-12's scoped `UsersPage`); this page is
 * the flat, unaffiliated-included overview an admin gets nowhere else.
 *
 * Admin only — `RequireManage` plus `src/manage/navigation.ts`'s
 * `visible: (p) => p.isAdmin` for the People section already gate this route,
 * so there is no permission branch here.
 */
export function UsersPage() {
  const { t } = useTranslation();
  const users = useAsync(() => apiGet('/users/all') as unknown as Promise<PlatformUser[]>, []);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('manage.usersAll.title')}
        actions={
          <Button as={Link} to="/manage/users/link">
            {t('manage.usersAll.linkIdentities')}
          </Button>
        }
      />

      <AsyncSection state={users} label={t('manage.usersAll.rowsLabel')} skeleton="table">
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState text={t('manage.usersAll.empty')} />
          ) : (
            <DataTable
              rowKey={(user) => user.user_id}
              rows={rows}
              columns={[
                {
                  key: 'name',
                  header: t('manage.usersAll.name'),
                  render: (user) => (
                    <Link to={`/users/${user.user_id}`}>
                      {user.name ?? user.email ?? t('manage.usersAll.view')}
                    </Link>
                  ),
                },
                {
                  key: 'email',
                  header: t('manage.usersAll.email'),
                  render: (user) => user.email ?? '—',
                },
                {
                  key: 'language',
                  header: t('manage.usersAll.language'),
                  render: (user) => user.preferred_language ?? '—',
                },
                {
                  key: 'login',
                  header: t('manage.usersAll.login'),
                  render: (user) =>
                    user.has_login ? t('manage.usersAll.yes') : t('manage.usersAll.no'),
                },
              ]}
            />
          )
        }
      </AsyncSection>
    </section>
  );
}
